import { BandChart } from '@toggle/design-system';
import { CalculatorResponse } from '@toggle/toggle';
import React, { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getHorizonToIndexMap } from '~/components/price-movement/components/price-movement-chart/utils/price-movement-chart-utils';
import { useThemePerformance } from '~/react-query-hooks/themes/useThemeOverview';
import { useFlags } from '~/stores/use-flags/useFlags';
import { useLanguageStore } from '~/stores/use-language-store/useLanguageStore';
import { useChartTheme } from '~/views/turbo-chart/hooks/use-chart-theme/useChartTheme';

import { DrawerSection } from '../drawer-section/DrawerSection';
import { HorizonSelector } from './horizon-selector/HorizonSelector';
import { OneDayReturnPill } from './one-day-return-pill/OneDayReturnPill';
import { RevampOneDayReturnPill } from './one-day-return-pill/RevampOneDayReturnPill';
import styles from './ThemePerformanceSection.module.scss';

export type Horizon = '1D' | '1W' | '1M' | '3M' | '6M' | '1Y';

type ThemePerformanceSectionProps = {
  entityTags: string[];
  customHeaderTitle?: ReactNode;
  customHeaderDescription?: ReactNode;
  isFetching?: boolean;
  isError?: boolean;
  error?: Error | null;
  themeId: string;
};

export const ThemePerformanceSection = ({
  entityTags,
  customHeaderDescription,
  customHeaderTitle,
  isFetching,
  isError,
  error = null,
  themeId,
}: ThemePerformanceSectionProps) => {
  const { t } = useTranslation('widget');
  const { colors } = useChartTheme(['soft']);
  const languageSetting = useLanguageStore(state => state.languageSetting);
  const [selectedHorizon, setSelectedHorizon] = useState<Horizon>('1Y');
  const intradayThemePerfV1Enabled = useFlags(state =>
    state.getFeatureFlagState('intraday_theme_perf_v1')
  );
  const horizonToIndexMap = getHorizonToIndexMap(1);

  const {
    data: themePerformanceData,
    isFetching: isThemePerformanceFetching,
    isError: isThemePerformanceError,
    error: themePerformanceError,
  } = useThemePerformance(
    entityTags,
    horizonToIndexMap.get(selectedHorizon.toLowerCase())
  );

  const total = entityTags.length > 10 ? 10 : entityTags.length;

  const formatData = (data?: CalculatorResponse) =>
    data?.aggregate.map((d, idx) => ({
      index: idx,
      value: d.value,
      date: new Date(d.index),
    })) ?? [];

  const handleHorizonChange = (horizon: Horizon) => {
    setSelectedHorizon(horizon);
  };

  const bandChartWrapperStyle: Record<string, string> = useMemo(
    () => ({
      '--band-chart-line-color': colors.default.value,
    }),
    [colors.default.value]
  );

  return (
    <DrawerSection
      isLoading={isThemePerformanceFetching || !!isFetching}
      isError={isThemePerformanceError || !!isError}
      isEmpty={!themePerformanceData?.aggregate.length}
      headerTitle={
        customHeaderTitle ?? t('themeOverview.deprecatedThemePerformanceTitle')
      }
      headerDescription={
        customHeaderDescription ??
        `${t('themeOverview.equalWeightedBasket')} ${t(
          'themeOverview.topCompanies',
          { total }
        )}`
      }
      rightHeaderNode={
        intradayThemePerfV1Enabled ? (
          <RevampOneDayReturnPill themeId={themeId} />
        ) : (
          <OneDayReturnPill themeId={themeId} />
        )
      }
      error={!!isError ? error : themePerformanceError}
    >
      <div className={styles.chartAndHorizonsWrapper}>
        <div className={styles.bandChartWrapper} style={bandChartWrapperStyle}>
          <BandChart
            tsData={formatData(themePerformanceData)}
            showLine
            domainLimited={false}
            includeXScalePadding={false}
            isBordered
            fillColors={{
              positive: colors.threshold.positive.value,
              negative: colors.threshold.negative.value,
            }}
            isPercent
            locale={languageSetting}
          />
        </div>
        <HorizonSelector
          selectedHorizon={selectedHorizon}
          horizons={['1W', '1M', '3M', '6M', '1Y']}
          handleHorizonChange={handleHorizonChange}
          horizonToIndexMap={horizonToIndexMap}
          themeId={themeId}
        />
      </div>
    </DrawerSection>
  );
};
